/**
 * Values API client
 *
 * Provides methods for interacting with the Values Service API.
 */

import { fetchApi } from './client';
import type {
  Value,
  ValueListResponse,
  ValueStatsResponse,
  CreateValueInput,
  UpdateValueInput,
  ValueEvidence,
  RecordEvidenceInput,
  GuidanceResponse,
  AutoTasksResponse,
} from '@/types/value';

// Values API base path - routes through BFF proxy for auth headers
// Note: fetchApi() prepends /api automatically, so just use /values
const VALUES_API_BASE = '/values';

/**
 * List all values for the current user
 */
export async function listValues(isActive?: boolean): Promise<ValueListResponse> {
  const query = isActive !== undefined ? `?is_active=${isActive}` : '';
  return fetchApi<ValueListResponse>(`${VALUES_API_BASE}${query}`);
}

/**
 * Get a single value by ID
 */
export async function getValue(valueId: string): Promise<{ value: Value }> {
  return fetchApi<{ value: Value }>(`${VALUES_API_BASE}/${valueId}`);
}

/**
 * Create a new value
 */
export async function createValue(input: CreateValueInput): Promise<{ value: Value }> {
  return fetchApi<{ value: Value }>(VALUES_API_BASE, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Update an existing value
 */
export async function updateValue(
  valueId: string,
  input: UpdateValueInput
): Promise<{ value: Value }> {
  return fetchApi<{ value: Value }>(`${VALUES_API_BASE}/${valueId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * Delete a value (soft delete)
 */
export async function deleteValue(valueId: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`${VALUES_API_BASE}/${valueId}`, {
    method: 'DELETE',
  });
}

/**
 * Get values statistics
 */
export async function getValuesStats(): Promise<ValueStatsResponse> {
  return fetchApi<ValueStatsResponse>(`${VALUES_API_BASE}/stats`);
}

/**
 * Record evidence of living or violating a value
 */
export async function recordEvidence(
  valueId: string,
  input: RecordEvidenceInput
): Promise<{ success: boolean; newScore: number }> {
  return fetchApi<{ success: boolean; newScore: number }>(
    `${VALUES_API_BASE}/${valueId}/evidence`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  );
}

/**
 * Get evidence history for a value
 */
export async function getEvidence(
  valueId: string,
  limit: number = 30
): Promise<{ evidence: ValueEvidence[] }> {
  return fetchApi<{ evidence: ValueEvidence[] }>(
    `${VALUES_API_BASE}/${valueId}/evidence?limit=${limit}`
  );
}

/**
 * Create auto-tasks for neglected values
 */
export async function createAutoTasks(): Promise<AutoTasksResponse> {
  return fetchApi<AutoTasksResponse>(`${VALUES_API_BASE}/auto-tasks`, {
    method: 'POST',
  });
}

/**
 * Get guidance content
 */
export async function getGuidance(topic?: string): Promise<GuidanceResponse> {
  const path = topic
    ? `${VALUES_API_BASE}/guidance/${topic}`
    : `${VALUES_API_BASE}/guidance`;
  return fetchApi<GuidanceResponse>(path);
}

/**
 * Seed values (development only)
 */
export async function seedValues(): Promise<{ created: string[]; count: number }> {
  return fetchApi<{ created: string[]; count: number }>(`${VALUES_API_BASE}/seed`, {
    method: 'POST',
  });
}

// =============================================================================
// SUPPORTING TASKS (Task-Value Edges)
// =============================================================================

/**
 * Supporting task info (edge data with task ID)
 */
export interface SupportingTask {
  edgeId: string;
  taskId: string;
  strength?: number;
  source?: string;
  createdAt?: string;
}

/**
 * Response for supporting tasks query
 */
export interface SupportingTasksResponse {
  valueId: string;
  valueName: string;
  supportingTasks: SupportingTask[];
  count: number;
}

/**
 * Get all tasks that support (serve) a value
 */
export async function getSupportingTasks(valueId: string): Promise<SupportingTasksResponse> {
  return fetchApi<SupportingTasksResponse>(`${VALUES_API_BASE}/${valueId}/tasks`);
}

/**
 * Remove a task-value association (delete the SERVES edge)
 */
export async function removeSupportingTask(
  valueId: string,
  taskId: string
): Promise<{ success: boolean; valueId: string; taskId: string }> {
  return fetchApi<{ success: boolean; valueId: string; taskId: string }>(
    `${VALUES_API_BASE}/${valueId}/tasks/${taskId}`,
    { method: 'DELETE' }
  );
}

